/**
 * Copyright (c) 2023 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Gianluca Tomasello <giagitom@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { ParamDefinition as PD } from '../../../mol-util/param-definition';
import { Vec3 } from '../../../mol-math/linear-algebra';
import { Unit } from '../../../mol-model/structure';
import { Mesh } from '../../../mol-geo/geometry/mesh/mesh';
import { MeshBuilder } from '../../../mol-geo/geometry/mesh/mesh-builder';
import { Segmentation } from '../../../mol-data/int';
import { isNucleic } from '../../../mol-model/structure/model/types';
import { addCylinder } from '../../../mol-geo/geometry/mesh/builder/cylinder';
import { UnitsMeshParams, UnitsMeshVisual, UnitsCylindersParams, UnitsCylindersVisual } from '../units-visual';
import { NucleotideLocationIterator, getNucleotideElementLoci, eachNucleotideElement, getNucleotideBaseType, createNucleicIndices, setSugarIndices, hasSugarIndices, setPurinIndices, hasPurinIndices, setPyrimidineIndices, hasPyrimidineIndices } from './util/nucleotide';
import { BaseGeometry } from '../../../mol-geo/geometry/base';
import { Sphere3D } from '../../../mol-math/geometry';
import { Cylinders } from '../../../mol-geo/geometry/cylinders/cylinders';
import { CylindersBuilder } from '../../../mol-geo/geometry/cylinders/cylinders-builder';
var pTrace = Vec3();
var pN1 = Vec3();
var pC2 = Vec3();
var pN3 = Vec3();
var pC4 = Vec3();
var pC5 = Vec3();
var pC6 = Vec3();
var pN7 = Vec3();
var pC8 = Vec3();
var pN9 = Vec3();
var pC1_1 = Vec3();
var pC2_1 = Vec3();
var pC3_1 = Vec3();
var pC4_1 = Vec3();
var pO4_1 = Vec3();
export var NucleotideAtomicBondParams = __assign(__assign(__assign({}, UnitsMeshParams), UnitsCylindersParams), { sizeFactor: PD.Numeric(0.3, { min: 0, max: 10, step: 0.01 }), radialSegments: PD.Numeric(16, { min: 2, max: 56, step: 2 }, BaseGeometry.CustomQualityParamInfo), tryUseImpostor: PD.Boolean(true) });
export function NucleotideAtomicBondVisual(materialId, structure, props, webgl) {
    return props.tryUseImpostor && webgl && webgl.extensions.fragDepth
        ? NucleotideAtomicBondImpostorVisual(materialId)
        : NucleotideAtomicBondMeshVisual(materialId);
}
function createNucleotideAtomicBondImpostor(ctx, unit, structure, theme, props, cylinders) {
    if (!Unit.isAtomic(unit))
        return Cylinders.createEmpty(cylinders);
    var nucleotideElementCount = unit.nucleotideElements.length;
    if (!nucleotideElementCount)
        return Cylinders.createEmpty(cylinders);
    var cylindersCountEstimate = nucleotideElementCount * 15; // 15 is the average purine (17) & pirimidine (13) bonds
    var builder = CylindersBuilder.create(cylindersCountEstimate, cylindersCountEstimate / 4, cylinders);
    var elements = unit.elements, model = unit.model;
    var _a = model.atomicHierarchy, chainAtomSegments = _a.chainAtomSegments, residueAtomSegments = _a.residueAtomSegments;
    var moleculeType = model.atomicHierarchy.derived.residue.moleculeType;
    var pos = unit.conformation.invariantPosition;
    var chainIt = Segmentation.transientSegments(chainAtomSegments, elements);
    var residueIt = Segmentation.transientSegments(residueAtomSegments, elements);
    var i = 0;
    while (chainIt.hasNext) {
        residueIt.setSegment(chainIt.move());
        while (residueIt.hasNext) {
            var residueIndex = residueIt.move().index;
            if (isNucleic(moleculeType[residueIndex])) {
                var idx = createNucleicIndices();
                setSugarIndices(idx, unit, residueIndex);
                if (hasSugarIndices(idx)) {
                    pos(idx.C1_1, pC1_1);
                    pos(idx.C2_1, pC2_1);
                    pos(idx.C3_1, pC3_1);
                    pos(idx.C4_1, pC4_1);
                    pos(idx.O4_1, pO4_1);
                    // trace cylinder
                    pos(idx.trace, pTrace);
                    builder.add(pC3_1[0], pC3_1[1], pC3_1[2], pTrace[0], pTrace[1], pTrace[2], 1, true, true, i);
                    // sugar ring
                    builder.add(pC3_1[0], pC3_1[1], pC3_1[2], pC4_1[0], pC4_1[1], pC4_1[2], 1, true, true, i);
                    builder.add(pC4_1[0], pC4_1[1], pC4_1[2], pO4_1[0], pO4_1[1], pO4_1[2], 1, true, true, i);
                    builder.add(pO4_1[0], pO4_1[1], pO4_1[2], pC1_1[0], pC1_1[1], pC1_1[2], 1, true, true, i);
                    builder.add(pC1_1[0], pC1_1[1], pC1_1[2], pC2_1[0], pC2_1[1], pC2_1[2], 1, true, true, i);
                    builder.add(pC2_1[0], pC2_1[1], pC2_1[2], pC3_1[0], pC3_1[1], pC3_1[2], 1, true, true, i);
                }
                var _b = getNucleotideBaseType(unit, residueIndex), isPurine = _b.isPurine, isPyrimidine = _b.isPyrimidine;
                if (isPurine) {
                    setPurinIndices(idx, unit, residueIndex);
                    if (idx.C1_1 !== -1 && idx.N9 !== -1) {
                        pos(idx.C1_1, pC1_1);
                        pos(idx.N9, pN9);
                        builder.add(pN9[0], pN9[1], pN9[2], pC1_1[0], pC1_1[1], pC1_1[2], 1, true, true, i);
                    }
                    else if (idx.N9 !== -1 && idx.trace !== -1) {
                        pos(idx.N9, pN9);
                        pos(idx.trace, pTrace);
                        builder.add(pN9[0], pN9[1], pN9[2], pTrace[0], pTrace[1], pTrace[2], 1, true, true, i);
                    }
                    if (hasPurinIndices(idx)) {
                        pos(idx.N1, pN1);
                        pos(idx.C2, pC2);
                        pos(idx.N3, pN3);
                        pos(idx.C4, pC4);
                        pos(idx.C5, pC5);
                        pos(idx.C6, pC6);
                        pos(idx.N7, pN7);
                        pos(idx.C8, pC8);
                        pos(idx.N9, pN9);
                        // base ring
                        builder.add(pN9[0], pN9[1], pN9[2], pC8[0], pC8[1], pC8[2], 1, true, true, i);
                        builder.add(pC8[0], pC8[1], pC8[2], pN7[0], pN7[1], pN7[2], 1, true, true, i);
                        builder.add(pN7[0], pN7[1], pN7[2], pC5[0], pC5[1], pC5[2], 1, true, true, i);
                        builder.add(pC5[0], pC5[1], pC5[2], pC6[0], pC6[1], pC6[2], 1, true, true, i);
                        builder.add(pC6[0], pC6[1], pC6[2], pN1[0], pN1[1], pN1[2], 1, true, true, i);
                        builder.add(pN1[0], pN1[1], pN1[2], pC2[0], pC2[1], pC2[2], 1, true, true, i);
                        builder.add(pC2[0], pC2[1], pC2[2], pN3[0], pN3[1], pN3[2], 1, true, true, i);
                        builder.add(pN3[0], pN3[1], pN3[2], pC4[0], pC4[1], pC4[2], 1, true, true, i);
                        builder.add(pC4[0], pC4[1], pC4[2], pC5[0], pC5[1], pC5[2], 1, true, true, i);
                        builder.add(pC4[0], pC4[1], pC4[2], pN9[0], pN9[1], pN9[2], 1, true, true, i);
                    }
                }
                else if (isPyrimidine) {
                    setPyrimidineIndices(idx, unit, residueIndex);
                    if (idx.C1_1 !== -1 && idx.N1 !== -1) {
                        pos(idx.N1, pN1);
                        pos(idx.C1_1, pC1_1);
                        builder.add(pN1[0], pN1[1], pN1[2], pC1_1[0], pC1_1[1], pC1_1[2], 1, true, true, i);
                    }
                    else if (idx.N1 !== -1 && idx.trace !== -1) {
                        pos(idx.N1, pN1);
                        pos(idx.trace, pTrace);
                        builder.add(pN1[0], pN1[1], pN1[2], pTrace[0], pTrace[1], pTrace[2], 1, true, true, i);
                    }
                    if (hasPyrimidineIndices(idx)) {
                        pos(idx.N1, pN1);
                        pos(idx.C2, pC2);
                        pos(idx.N3, pN3);
                        pos(idx.C4, pC4);
                        pos(idx.C5, pC5);
                        pos(idx.C6, pC6);
                        // base ring
                        builder.add(pN1[0], pN1[1], pN1[2], pC6[0], pC6[1], pC6[2], 1, true, true, i);
                        builder.add(pC6[0], pC6[1], pC6[2], pC5[0], pC5[1], pC5[2], 1, true, true, i);
                        builder.add(pC5[0], pC5[1], pC5[2], pC4[0], pC4[1], pC4[2], 1, true, true, i);
                        builder.add(pC4[0], pC4[1], pC4[2], pN3[0], pN3[1], pN3[2], 1, true, true, i);
                        builder.add(pN3[0], pN3[1], pN3[2], pC2[0], pC2[1], pC2[2], 1, true, true, i);
                        builder.add(pC2[0], pC2[1], pC2[2], pN1[0], pN1[1], pN1[2], 1, true, true, i);
                    }
                }
                ++i;
            }
        }
    }
    var c = builder.getCylinders();
    var sphere = Sphere3D.expand(Sphere3D(), unit.boundary.sphere, 1 * props.sizeFactor);
    c.setBoundingSphere(sphere);
    return c;
}
export function NucleotideAtomicBondImpostorVisual(materialId) {
    return UnitsCylindersVisual({
        defaultProps: PD.getDefaultValues(NucleotideAtomicBondParams),
        createGeometry: createNucleotideAtomicBondImpostor,
        createLocationIterator: NucleotideLocationIterator.fromGroup,
        getLoci: getNucleotideElementLoci,
        eachLocation: eachNucleotideElement,
        setUpdateState: function (state, newProps, currentProps) {
            state.createGeometry = (newProps.sizeFactor !== currentProps.sizeFactor);
        },
        mustRecreate: function (structureGroup, props, webgl) {
            return !props.tryUseImpostor || !webgl;
        }
    }, materialId);
}
function createNucleotideAtomicBondMesh(ctx, unit, structure, theme, props, mesh) {
    if (!Unit.isAtomic(unit))
        return Mesh.createEmpty(mesh);
    var nucleotideElementCount = unit.nucleotideElements.length;
    if (!nucleotideElementCount)
        return Mesh.createEmpty(mesh);
    var sizeFactor = props.sizeFactor, radialSegments = props.radialSegments;
    var vertexCount = nucleotideElementCount * (radialSegments * 15); // 15 is the average purine (17) & pirimidine (13) bonds
    var builderState = MeshBuilder.createState(vertexCount, vertexCount / 4, mesh);
    var elements = unit.elements, model = unit.model;
    var _a = model.atomicHierarchy, chainAtomSegments = _a.chainAtomSegments, residueAtomSegments = _a.residueAtomSegments;
    var moleculeType = model.atomicHierarchy.derived.residue.moleculeType;
    var pos = unit.conformation.invariantPosition;
    var chainIt = Segmentation.transientSegments(chainAtomSegments, elements);
    var residueIt = Segmentation.transientSegments(residueAtomSegments, elements);
    var cylinderProps = { radiusTop: 1 * sizeFactor, radiusBottom: 1 * sizeFactor, radialSegments: radialSegments };
    var i = 0;
    while (chainIt.hasNext) {
        residueIt.setSegment(chainIt.move());
        while (residueIt.hasNext) {
            var residueIndex = residueIt.move().index;
            if (isNucleic(moleculeType[residueIndex])) {
                var idx = createNucleicIndices();
                builderState.currentGroup = i;
                setSugarIndices(idx, unit, residueIndex);
                if (hasSugarIndices(idx)) {
                    pos(idx.C1_1, pC1_1);
                    pos(idx.C2_1, pC2_1);
                    pos(idx.C3_1, pC3_1);
                    pos(idx.C4_1, pC4_1);
                    pos(idx.O4_1, pO4_1);
                    // trace cylinder
                    pos(idx.trace, pTrace);
                    addCylinder(builderState, pC3_1, pTrace, 1, cylinderProps);
                    // sugar ring
                    addCylinder(builderState, pC3_1, pC4_1, 1, cylinderProps);
                    addCylinder(builderState, pC4_1, pO4_1, 1, cylinderProps);
                    addCylinder(builderState, pO4_1, pC1_1, 1, cylinderProps);
                    addCylinder(builderState, pC1_1, pC2_1, 1, cylinderProps);
                    addCylinder(builderState, pC2_1, pC3_1, 1, cylinderProps);
                }
                var _b = getNucleotideBaseType(unit, residueIndex), isPurine = _b.isPurine, isPyrimidine = _b.isPyrimidine;
                if (isPurine) {
                    setPurinIndices(idx, unit, residueIndex);
                    if (idx.C1_1 !== -1 && idx.N9 !== -1) {
                        pos(idx.C1_1, pC1_1);
                        pos(idx.N9, pN9);
                        addCylinder(builderState, pN9, pC1_1, 1, cylinderProps);
                    }
                    else if (idx.N9 !== -1 && idx.trace !== -1) {
                        pos(idx.N9, pN9);
                        pos(idx.trace, pTrace);
                        addCylinder(builderState, pN9, pTrace, 1, cylinderProps);
                    }
                    if (hasPurinIndices(idx)) {
                        pos(idx.N1, pN1);
                        pos(idx.C2, pC2);
                        pos(idx.N3, pN3);
                        pos(idx.C4, pC4);
                        pos(idx.C5, pC5);
                        pos(idx.C6, pC6);
                        pos(idx.N7, pN7);
                        pos(idx.C8, pC8);
                        pos(idx.N9, pN9);
                        // base ring
                        addCylinder(builderState, pN9, pC8, 1, cylinderProps);
                        addCylinder(builderState, pC8, pN7, 1, cylinderProps);
                        addCylinder(builderState, pN7, pC5, 1, cylinderProps);
                        addCylinder(builderState, pC5, pC6, 1, cylinderProps);
                        addCylinder(builderState, pC6, pN1, 1, cylinderProps);
                        addCylinder(builderState, pN1, pC2, 1, cylinderProps);
                        addCylinder(builderState, pC2, pN3, 1, cylinderProps);
                        addCylinder(builderState, pN3, pC4, 1, cylinderProps);
                        addCylinder(builderState, pC4, pC5, 1, cylinderProps);
                        addCylinder(builderState, pC4, pN9, 1, cylinderProps);
                    }
                }
                else if (isPyrimidine) {
                    setPyrimidineIndices(idx, unit, residueIndex);
                    if (idx.C1_1 !== -1 && idx.N1 !== -1) {
                        pos(idx.N1, pN1);
                        pos(idx.C1_1, pC1_1);
                        addCylinder(builderState, pN1, pC1_1, 1, cylinderProps);
                    }
                    else if (idx.N1 !== -1 && idx.trace !== -1) {
                        pos(idx.N1, pN1);
                        pos(idx.trace, pTrace);
                        addCylinder(builderState, pN1, pTrace, 1, cylinderProps);
                    }
                    if (hasPyrimidineIndices(idx)) {
                        pos(idx.N1, pN1);
                        pos(idx.C2, pC2);
                        pos(idx.N3, pN3);
                        pos(idx.C4, pC4);
                        pos(idx.C5, pC5);
                        pos(idx.C6, pC6);
                        // base ring
                        addCylinder(builderState, pN1, pC6, 1, cylinderProps);
                        addCylinder(builderState, pC6, pC5, 1, cylinderProps);
                        addCylinder(builderState, pC5, pC4, 1, cylinderProps);
                        addCylinder(builderState, pC4, pN3, 1, cylinderProps);
                        addCylinder(builderState, pN3, pC2, 1, cylinderProps);
                        addCylinder(builderState, pC2, pN1, 1, cylinderProps);
                    }
                }
                ++i;
            }
        }
    }
    var m = MeshBuilder.getMesh(builderState);
    var sphere = Sphere3D.expand(Sphere3D(), unit.boundary.sphere, 1 * props.sizeFactor);
    m.setBoundingSphere(sphere);
    return m;
}
export function NucleotideAtomicBondMeshVisual(materialId) {
    return UnitsMeshVisual({
        defaultProps: PD.getDefaultValues(NucleotideAtomicBondParams),
        createGeometry: createNucleotideAtomicBondMesh,
        createLocationIterator: NucleotideLocationIterator.fromGroup,
        getLoci: getNucleotideElementLoci,
        eachLocation: eachNucleotideElement,
        setUpdateState: function (state, newProps, currentProps) {
            state.createGeometry = (newProps.sizeFactor !== currentProps.sizeFactor ||
                newProps.radialSegments !== currentProps.radialSegments);
        },
        mustRecreate: function (structureGroup, props, webgl) {
            return props.tryUseImpostor && !!webgl;
        }
    }, materialId);
}
